import './Home.scss';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Grid } from '@mui/material';
import AcStats from '../../components/AcStats/AcStats';
import CountdownTimer from '../../components/CountdownTimer/CountdownTimer';
import ImageSlider from '../../components/ImageSlider/ImageSlider';
import Loader from '../../components/Loader/Loader';
import Notifications from '../../components/Notifications/Notifications';
import ScheduleDatesHome from '../../components/ScheduleDatesHome/ScheduleDatesHome';
import { pairingStatus } from '../../constants/bindings';
import { months } from '../../constants/dates';
import { pushSiteInfo, registerPageInfo } from '../../helpers/analyticsHelper';
import {
  apiDateToDate,
  getEasternTime,
  stringToMonthDate
} from '../../helpers/dateHelpers';
import { FreezePeriodInterface } from '../../models/FreezePeriodInterface';
import { useAcStats } from '../../providers/AcStats';
import { useAuth } from '../../providers/Auth';
import { useFreezePeriod } from '../../providers/FreezePeriod';
import { useNotifications } from '../../providers/Notifications';
import { useProfile } from '../../providers/Profile';

const Home = () => {
  const AcStatsContext = useAcStats();
  const user = useAuth();
  const FreezePeriodContext = useFreezePeriod();
  const NotificationsContext = useNotifications();
  const profileContext = useProfile();
  const { t } = useTranslation();
  const [currentPairingMonthData, setCurrentPairingMonthData] =
    useState<FreezePeriodInterface>();
  const [biddingClosedWithTime, setBiddingClosedWithTime] = useState(
    new Date()
  );
  const [biddingClosed, setBiddingClosed] = useState(new Date());
  const [biddingClosedMonth, setBiddingClosedMonth] = useState('');
  const [scheduleReleasedMonth, setScheduleReleasedMonth] = useState('');

  useEffect(() => {
    if (!currentPairingMonthData) return;

    const currentTimeInEst = getEasternTime().getTime();
    const biddingClosed = apiDateToDate(
      currentPairingMonthData.biddingClosed?.start
    );
    const biddingClosedWithTime = apiDateToDate(
      currentPairingMonthData.biddingClosed?.start,
      currentPairingMonthData.biddingClosed?.time
    );
    const biddingClosingTime = biddingClosedWithTime?.getTime();
    setBiddingClosed(biddingClosed);
    setBiddingClosedWithTime(biddingClosedWithTime);
    setBiddingClosedMonth(
      currentTimeInEst >= biddingClosingTime
        ? months[currentPairingMonthData.pairingMonth - 1]
        : months[biddingClosed.getMonth()]
    );
    setScheduleReleasedMonth(
      currentTimeInEst >= biddingClosingTime
        ? months[currentPairingMonthData.pairingMonth - 1]
        : months[biddingClosed.getMonth()]
    );
  }, [currentPairingMonthData]);

  const convertTime = (apiTime) => {
    return apiTime.substr(0, 5);
  };

  useEffect(() => {
    profileContext.getProfile();
    NotificationsContext.getNotifications();
    if (profileContext?.profile?.results) {
      FreezePeriodContext.getFreezePeriod();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      !FreezePeriodContext.freezePeriodData.pairingFreeze ||
      FreezePeriodContext.loading
    ) {
      return;
    }
    setCurrentPairingMonthData({
      ...FreezePeriodContext.freezePeriodData.pairingFreeze.filter(
        (val) => val
      )[0]
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [FreezePeriodContext]);

  useEffect(() => {
    if (user.loading || !profileContext.profile.results) {
      return;
    }
    pushSiteInfo(user.user, profileContext.profile);
    registerPageInfo('my access app|homepage', {
      level1: 'my access app',
      level2: 'homepage',
      level3: null,
      level4: null
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, profileContext.profile.results]);

  return AcStatsContext.loading ||
    FreezePeriodContext.loading ||
    NotificationsContext.loading ? (
    <Loader />
  ) : (
    <div className="home-page">
      <div className="hero-banner-container"></div>
      <div className="home-page-content">
        <h1>
          {t('home.hi')} {user.user ? user.user.attributes['given_name'] : ''}!
        </h1>

        <Grid container spacing={3} direction="row">
          <Grid item className="side-bar" xs={4}>
            <div className="full-height">
              <span className="sub-heading">{t('home.system-status')}</span>

              <div className="bidding">
                {currentPairingMonthData?.pairingStatus ===
                pairingStatus.SCHEDULED ? (
                  <div className="bg-purple">
                    {' '}
                    {t('home.schedule-released', {
                      month: scheduleReleasedMonth
                    })}{' '}
                    <i className="icon-calendar"></i>
                  </div>
                ) : currentPairingMonthData?.pairingStatus ===
                    pairingStatus.POSTED &&
                  currentPairingMonthData?.dbmPosted ? (
                  <div className="bg-green">
                    {' '}
                    <div className="status-message">
                      <span>{t('home.bidding-open')}</span>{' '}
                      <Divider
                        className="vertical-line"
                        orientation="vertical"
                        flexItem
                      />{' '}
                      <span>{t('home.pairing-posted')}</span>
                    </div>{' '}
                    <i className="icon-clock home__bidding__clock--icon"></i>
                  </div>
                ) : currentPairingMonthData?.pairingStatus ===
                  pairingStatus.CLOSED ? (
                  <div className="bg-red">
                    <span>
                      {t('home.bidding-closed', { month: biddingClosedMonth })}
                    </span>
                    <i className="icon-lock" />
                  </div>
                ) : (
                  <div className="bg-red">
                    <span>
                      {t('home.bidding-closed', { month: biddingClosedMonth })}
                    </span>
                    <i className="icon-lock" />
                  </div>
                )}
                {currentPairingMonthData?.pairingStatus ===
                  pairingStatus.POSTED &&
                  currentPairingMonthData?.dbmPosted && (
                    <div className="bidding-timer">
                      <span>{t('home.bidding-closes-in')}:</span>
                      <CountdownTimer
                        biddingClosedWithTime={biddingClosedWithTime}
                        sdoAdminReport={false}
                      />
                      <span>
                        {t('home.on')} {stringToMonthDate(biddingClosed)},{' '}
                        {convertTime(
                          currentPairingMonthData?.biddingClosed?.time
                        )}{' '}
                        ET{' '}
                      </span>
                    </div>
                  )}
                {(currentPairingMonthData?.biddingClosed?.isAdjusted ||
                  currentPairingMonthData?.pairingPosted?.isAdjusted ||
                  currentPairingMonthData?.pairingFreezed?.isAdjusted) &&
                  currentPairingMonthData?.pairingStatus ===
                    pairingStatus.POSTED &&
                  currentPairingMonthData?.dbmPosted && (
                    <div className="time-adjusted-alert">
                      {t('home.time-adjusted')}
                    </div>
                  )}
                {currentPairingMonthData ? (
                  <ScheduleDatesHome
                    currentPairingMonthData={currentPairingMonthData}
                  />
                ) : (
                  ''
                )}
              </div>
            </div>
          </Grid>
          <Grid item className="content-right" xs={8}>
            <div className="full-height">
              <Grid container className="top-row" spacing={3} direction="row">
                <Grid item xs={6}>
                  <div className="full-height">
                    <span className="sub-heading">{t('home.whats-new')} </span>
                    <Notifications />
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div className="full-height">
                    <span className="sub-heading">{t('home.ac-stats')}</span>
                    <AcStats />
                  </div>
                </Grid>
              </Grid>

              <Grid
                container
                className="row-right-bottom"
                spacing={3}
                direction="row"
              >
                <Grid item xs={6}>
                  <div className="bg-dark-blue">
                    <div className="note-title">{t('home.note-title')}</div>
                    <div className="note-body1">{t('home.note-body-1')}</div>
                    <div className="note-body2">{t('home.note-body-2')}</div>
                  </div>
                </Grid>
                <Grid item xs={6} className="image-slider-grid">
                  <div className="image-slider">
                    <span className="sub-heading">
                      {t('home.image-slider-title')}
                    </span>
                    <ImageSlider />
                  </div>
                </Grid>
              </Grid>
            </div>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};
export default Home;
